/**
 * table-of-contents.js
 * Controls the functionality of the table of contents component
 */

// globals
let articleBody = null; // will store the content area element
let observeIntersections = null; // will store intersection observer
let observeIntersectionsFallbackTimer = null; // will store a timer for IE fallback
let observeStickyIntersections = null; // will store mutations observer
let toc = null; // will store table of contents element
let tocOriginalPosition = null;
let tocToggle = null; // will store main table of contents toggle button
let tocProgressBar = null; // will store the progressbars
let tocLabelElement = null; // will store label dom element
let tocListContainer = null; // will store outer list container of links
let tocList =  null; // will store the list element holding the links
let tocLinks = null; // will store all links in the table of contents

/**
 * run through checks and setup
 */
const setup = () => {

	// select toc
	toc = document.querySelector( '.msr-table-of-contents-block' );

	// maybe abandon
	if ( !toc ) return;

	tocOriginalPosition = toc.offsetTop;

	// select toggle
	tocToggle = toc.querySelector( '.msr-table-of-contents-block .btn-collapse' );

	// maybe abandon
	if ( !tocToggle ) return;

	// select main links container
	tocListContainer = toc.querySelector( '.msr-table-of-contents-block__collapse-wrapper' );

	// maybe abandon
	if ( !tocListContainer ) return;

	// Actual toc list element
	tocList = toc.querySelector( '.msr-table-of-contents-block__list' );

	// select all toc links
	tocLinks = toc.querySelectorAll( '.msr-table-of-contents-block__list-item-link' );

	// maybe abandon
	if ( !tocLinks.length ) return;

	// set toc label element
	tocLabelElement = toc.querySelector( '.msr-table-of-contents-block__current' );

	// all checks pass, initialize
	document.addEventListener( 'DOMContentLoaded', initialize );
};

/**
 * check if hash exists in URL
 * ie /#toc-heading-0
 */
const maybeInitialPageLoadWithHash = () =>
	toc && '' !== location.hash;

/**
 * used when initially loading the window, and detecting a hash
 * will wait for window load to scroll the page to the correct anchor while
 * preventing overlap. Depends on the progress status bar for the overall calculation
 * since the internals of the main TOC are positioned absolute, and therefore, don't
 * lend themselves to an overall height calculation.
 */
const doInitialWindowLoadWithHash = () => {
	const target = window.location.hash;
	const targetElement = document.querySelector( target );
	if ( targetElement ) {
		scrollWindowToElement( target );
	}
};

/**
 * apply bindings and listeners
 */
const initialize = () => {

	// watch toc for class changes when sticky via stickybits add-on
	createStickyIntersectionObserver();

	// create progressbar observer
	createProgressBarObserver();

	// Handle if hash exists on first time load
	if ( maybeInitialPageLoadWithHash() ) {

		doInitialWindowLoadWithHash();
	}

	// bind key listeners
	document.addEventListener( 'keyup', maybeCollapse );

	// bind toggle handler
	tocToggle.addEventListener( 'click', toggleList );

	// modify link behavior
	tocLinks.forEach( ( item ) => item.addEventListener( 'click', listItemClickHandler ) );

	// create intersection observer with IE11 fallback
	if ( window.IntersectionObserver ) {
		createIntersectionObserver();
	} else {
		window.addEventListener( 'scroll', intersectionObserverFallback );
	}

};

/**
 * mutation handler for mutationsList of the DOM element
 * being observed, which in our case id the toc element
 */
const stickyIntersectionHandler = entries => {
	[...entries].forEach( entry => {
		const { target, isIntersecting, intersectionRatio } = entry;

		/**
		 * We pad the wrapper's offset calculation by 10px to account for slight
		 * differences in positioning when stickiness is removed from the TOC.
		 **/
		const isTocInOriginalPosition = ( toc.offsetTop - 10 ) <= tocOriginalPosition;

		if ( isIntersecting ) {
			target.classList.add( 'js-is-sticky--stuck-to-top' );
			tocList.classList.add( 'col-12', 'col-lg-8', 'center' );
		}

		// When the TOC initially sticks to the top of the page, collapse it if it's expanded
		if(
			isIntersecting &&
			0.1 > intersectionRatio &&
			target.classList.contains( 'js-is-sticky--stuck-to-top' ) &&
			'true' === target.querySelector( '.btn.btn-collapse' ).getAttribute( 'aria-expanded' )
		) {
			observeStickyIntersections.unobserve( toc );
			getCollapseInstance().hide();
			// otherwise, expand it if we're at the top of the post content
		} else if (
			!isIntersecting &&
			isTocInOriginalPosition &&
			target.classList.contains( 'js-is-sticky--stuck-to-top' )
		) {
			// Stop oberserving for intersections while animating between states
			observeStickyIntersections.unobserve( toc );
			target.classList.remove( 'js-is-sticky--stuck-to-top' );
			tocList.classList.remove( 'col-12', 'col-lg-8', 'center' );

			// update our accordion's collapse state
			getCollapseInstance().show();

			// simple reset
			resetTocLinks();
		}
	} );

	// After state changes have completed, re-observe the TOC for sticky changes
	setTimeout( () => {
		observeStickyIntersections.observe( toc );
	}, 150 );
};

/**
 * Use an IntersectionObserver to determine when the TOC is sticky and on top of the viewport.
 * We do this vs. checking stickybits classes because stickybits classes only work when the TOC
 * is the first block.
 **/
const createStickyIntersectionObserver = () => {

	// If no MutationObserver, abandon
	if ( !window.IntersectionObserver ) return;

	// Change the intesection root if the user is logged in
	const rootMarginBottom = document.body.classList.contains( 'admin-bar' ) ? '-95%' : '-97%';

	// Create an observer instance linked to the callback function
	observeStickyIntersections = new IntersectionObserver(
		stickyIntersectionHandler,
		{
			root: null, // main viewport
			threshold: [0], // when 0% of the element is inside the root bounding box
			rootMargin: `-1% 0px ${rootMarginBottom} 0px`, // 24px from top of viewport
		},
	);
	// Start observing the target node for attribute changes
	observeStickyIntersections.observe( toc );
};

/**
 * disconnect from the mutations observer. Useful for when the toggle
 * button is clicked to expand and collapse the elements within it.
 */
const destroyStickyIntersectionObserver = () => {
	observeStickyIntersections.disconnect();
};

/**
 * make link active that matches specified id
 * @param {String} id - id of current intersection
 */
const makeTocLinkActive = id => {

	// set the activeLink
	const activeLink = toc.querySelector( `a[href="#${id}"]` );

	// make active
	activeLink.classList.add( 'is-toc-active' );

	// make aria-current active
	activeLink.setAttribute( 'aria-current', 'true' );

	// update the tocLabelElement
	if( tocLabelElement ) {
		tocLabelElement.innerText = activeLink.textContent;
	}
};

/**
 * reset link styles by removing active classes
 */
const resetTocLinks = () => {

	// spread operator allows compiling for IE11
	[...tocLinks].forEach(
		link => {
			link.classList.remove( 'is-toc-active' );
			link.removeAttribute( 'aria-current' );
		}
	);
};

/**
 * intersection handler for entries being observed
 * which in our case are all tocLink related sections
 * @param {Array} entries - entries that are being observed
 */
const intersectionHandler = entries => {

	// spread operator allows compiling for IE11
	[...entries].forEach( entry => {

		// destructure target element and status
		const { target, isIntersecting } = entry;

		// when isIntersecting is true, make it active
		// we only want one active, so last one in will become current
		if ( isIntersecting ) {

			// reset all
			resetTocLinks();

			// activate current
			makeTocLinkActive( target.id );

			return;
		}
	} );
};

/**
 * creates an intersection observer to detect current active section
 * If null, then proceed with a request timeout approach
 */
const createIntersectionObserver = () => {

	// assign a new observer and monitor how it intersects the viewport
	observeIntersections = new IntersectionObserver(
		intersectionHandler,
		{
			root: null, // main viewport
			rootMargin: '0px 0px -66% 0px', // 2/3rds
			threshold: [0.25] // 100% visible
		}
	);

	// push each qualified link target into the observer
	// spread operator allows compiling for IE11
	[...tocLinks].forEach( link => {

		// get attribute
		const maybeHasTargetAttribute = link.getAttribute( 'href' ).substring( 1 );

		// if attribute, attempt to get the target div element
		const targetElement = maybeHasTargetAttribute && document.getElementById( maybeHasTargetAttribute );

		if ( targetElement ) {

			// if target div element qualified, push to the observer
			observeIntersections.observe( targetElement );

			// add tabindex to target for focus logic
			targetElement.setAttribute( 'tabindex', '-1' );

			// add focusable class to make css modifications easier
			targetElement.classList.add( 'msr-toc__focusable' );
		}

	} );
};

/**
 * fallback when intersectionObserver is not available
 * browsers such as ie11 will use this method
 */
const intersectionObserverFallback = () => {

	// clear any existing timer
	clearTimeout( observeIntersectionsFallbackTimer );

	// create a new timer
	observeIntersectionsFallbackTimer = setTimeout( function() {

		// Initialize a null target element
		let foundTarget = null;

		// spread operator allows compiling for IE11
		// reverse to get the nearest element past the threshold
		[...tocLinks].reverse().find( link => {

			// set targetName from data attribute
			const targetName = link.getAttribute( 'href' );

			// threshold in rule of thirds
			const threshold = window.innerHeight * 0.33;

			// set target
			const target = targetName && document.getElementById( targetName ) || null;

			// if target exists, continue
			if ( target ) {

				// Quick reset
				resetTocLinks();

				// set position
				const position = target.getBoundingClientRect();

				// destructure top
				const { top } = position;

				// within 33% of top, rule of thirds
				if ( threshold > top ) {

					// set foundTarget (prevents us having to query again)
					foundTarget = target;

					// return truthy, break find
					return true;
				}
			}
		} );

		// If there is a match, make it active
		return foundTarget && makeTocLinkActive( foundTarget.id );

	}, 300 );
};

/**
 * handle the progress bar updates
 */
const progressBarHandler = () => {

	// set contentTopPosition based on content area
	const contentTopPosition = articleBody.getBoundingClientRect().top;

	// alter bottom position for progressbar based on expanded/collapsed
	if ( 'true' === tocToggle.getAttribute( 'aria-expanded' && toc.classList.contains( '.js-is-sticky--stuck-to-top' ) ) ) {
		tocProgressBar.style.bottom = `-${tocListContainer.offsetHeight}px`;
	} else {

		// when collapsed, reset progressbar to 0
		tocProgressBar.style.bottom = 0;
	}

	// If contentTopPosition is negative, continue (means its scrolling is active)
	if ( 0 >= contentTopPosition ) {

		// destructure offsetHeight
		const { offsetHeight } = articleBody;

		// set progressBarPercent
		const progressBarPercent = Math.round( Math.abs( contentTopPosition ) / offsetHeight * 100 );

		// update all progressBars widths
		tocProgressBar.style.width = `${progressBarPercent}%`;

	// else reset progressBars widths when not within scroll area
	} else {
		tocProgressBar.style.width = 0;
	}

	// loop back
	window.requestAnimationFrame( () => maybeIdleCallback( progressBarHandler ) );
};

/**
 * apply requestIdleCallback if available for performance
 * @param {Function} fn - function to wrap as idle
 * @returns {Function}
 */
const maybeIdleCallback = fn => {

	// If browser supports requestIdleCallback, use it
	if ( window.requestIdleCallback ) {
		return window.requestIdleCallback( fn, { timeout: 300 } );
	}

	// return default otherwise
	return fn();
};

/**
 * setup the progress bar observer
 */
const createProgressBarObserver = () => {

	// set articleBody
	articleBody = document.querySelector( '.single-post__content' );
	// if no body, abandon
	if ( !articleBody ) return;

	// set progressBar
	tocProgressBar = toc.querySelector( '.msr-table-of-contents-block__progress-bar' );

	// if no progressBars, abandon
	if ( !tocProgressBar ) return;

	// if no requestAnimationFrame, abandon
	if ( !window.requestAnimationFrame ) return;

	// begin loop
	window.requestAnimationFrame( () => maybeIdleCallback( progressBarHandler ) );
};

/**
 * Gets the MWF Collapse instance associated with the ToC block
 */
const getCollapseInstance = () => {
	// Get the MWF collapse instance
	if ( window.mwf.Collapse ) {
		const collapseInstances = window.mwf.Collapse.getInstances();
		if ( collapseInstances.length ) {
			const [collapseInstance] = collapseInstances.filter( instance => instance.el === tocListContainer );
			return collapseInstance;
		}
	}
	return null;
};

/**
 * toggle expand or collapse of tocLinks
 */
const toggleList = () => {

	// stop listening to mutations
	destroyStickyIntersectionObserver();

	const collapseInstance = getCollapseInstance();
	collapseInstance.toggle();
	toc.classList.toggle( 'msr-table-of-contents-block--expanded' );

	// restore listening to mutations
	createStickyIntersectionObserver();
};

/**
 * collapse container if escaped
 * @param {Object} e - Event Object
 */
const maybeCollapse = e => {

	// get keyCode from event
	const {keyCode} = e;

	// test if code is escape
	if( 27 === keyCode ) {

		const collapseInstance = getCollapseInstance();
		collapseInstance.hide();
		toc.classList.remove( 'msr-table-of-contents-block--expanded' );
	}
};

/**
 * scroll the window to a matching available target
 * @param {String} target - id string of element
 */
const scrollWindowToElement = target => {

	// set target element
	const scrollToElement = document.querySelector( target );

	// if target element calculate position and scroll
	if ( scrollToElement ) {

		// target y position
		const targetY = scrollToElement.getBoundingClientRect().top;

		// curreny body position
		const currentY = document.body.getBoundingClientRect().top;

		// toc height (to prevent hiding headline after scrolling)
		const tocHeight = toc.offsetHeight + 24; // add 24 for nice vertical padding

		// scroll window
		window.scrollTo( {
			top: targetY - currentY - tocHeight,
			left: 0,
			behavior: 'smooth'
		} );

		// apply focus
		scrollToElement.focus( { preventScroll: true } );
	}
};

/**
 * handler for when a tocLink is clicked
 * @param {Object} e - Event Object
 */
const listItemClickHandler = e => {

	// intercept the <a> click
	e.preventDefault();

	// destructure
	const { target } = e;

	// if delegated from an <a> element
	if (
		target &&
		target.tagName &&
		'a' === target.tagName.toLowerCase()
	) {
		// close toc list via toggleList method
		toggleList();

		// get href
		const dataTargetId = target.getAttribute( 'href' );

		// if href, handle it
		if ( dataTargetId ) {
			// We use history.pushState to update the URL without triggering the native hashchange scroll event
			const newUrl = window.location.hash ? window.location.href.substr( 0,window.location.href.indexOf( '#' ) ) + dataTargetId : window.location.href + dataTargetId;
			history.pushState( null, null, newUrl );
			scrollWindowToElement( dataTargetId );
		}
	}
};

export default setup;
